"use client";

import { useTranslation } from "@/i18n";
import {
    SectionWrapper,
    SectionEyebrow,
    SectionHeadline,
} from "@/components/ui/SectionWrapper";
import {
    FadeIn,
    FadeInStagger,
    FadeInStaggerChild,
} from "@/components/ui/AnimatedSection";
import { VideoClip } from "@/components/ui/VideoClip";

function PillarIcon({ icon }: { icon: string }) {
    const iconMap: Record<string, React.ReactNode> = {
        industry: (
            <svg viewBox="0 0 48 48" fill="none" className="w-12 h-12">
                <rect x="4" y="20" width="12" height="24" rx="1" stroke="currentColor" strokeWidth="1.5" />
                <rect x="18" y="12" width="12" height="32" rx="1" stroke="currentColor" strokeWidth="1.5" />
                <rect x="32" y="4" width="12" height="40" rx="1" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10 28v8M24 20v16M38 12v24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
        ),
        planet: (
            <svg viewBox="0 0 48 48" fill="none" className="w-12 h-12">
                <circle cx="24" cy="24" r="18" stroke="currentColor" strokeWidth="1.5" />
                <ellipse cx="24" cy="24" rx="8" ry="18" stroke="currentColor" strokeWidth="1.5" />
                <path d="M6 24h36" stroke="currentColor" strokeWidth="1.5" />
                <path d="M9 14h30M9 34h30" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
        ),
        ecology: (
            <svg viewBox="0 0 48 48" fill="none" className="w-12 h-12">
                <path d="M24 44V24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M24 24c0-10 8-18 18-20-2 10-10 18-18 20z" stroke="currentColor" strokeWidth="1.5" />
                <path d="M24 32c0-8-6-14-14-16 2 8 8 14 14 16z" stroke="currentColor" strokeWidth="1.5" />
                <circle cx="24" cy="44" r="2" fill="currentColor" />
            </svg>
        ),
    };

    return (
        <div className="text-mars">{iconMap[icon] || iconMap.industry}</div>
    );
}

const PILLAR_META = [
    { number: "01", icon: "industry" },
    { number: "02", icon: "planet" },
    { number: "03", icon: "ecology" },
] as const;

export function Pillars() {
    const { t } = useTranslation();
    return (
        <SectionWrapper id="pillars" className="py-(--section-y-lg)">
            <div className="text-center max-w-3xl mx-auto mb-16">
                <FadeIn>
                    <SectionEyebrow className="mx-auto text-center">
                        {t.pillars.eyebrow}
                    </SectionEyebrow>
                    <SectionHeadline className="text-center">
                        {t.pillars.headline}
                    </SectionHeadline>
                    <p className="text-lg text-text-secondary leading-relaxed">
                        {t.pillars.description}
                    </p>
                </FadeIn>
            </div>

            <FadeInStagger className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
                {t.pillars.items.map((pillar, i) => (
                    <FadeInStaggerChild key={PILLAR_META[i].number}>
                        <div className="group relative p-8 rounded-2xl bg-surface border border-border hover:border-mars/30 transition-all duration-500 h-full">
                            <div className="absolute inset-0 rounded-2xl bg-mars/3 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            <div className="relative">
                                <div className="flex items-start justify-between mb-6">
                                    <span className="text-5xl font-bold text-border-light group-hover:text-border transition-colors">
                                        {PILLAR_META[i].number}
                                    </span>
                                    <PillarIcon icon={PILLAR_META[i].icon} />
                                </div>
                                <h3 className="text-xl font-bold text-text mb-1 group-hover:text-mars-light transition-colors">
                                    {pillar.title}
                                </h3>
                                <p className="text-sm font-medium text-mars mb-4">
                                    {pillar.subtitle}
                                </p>
                                <p className="text-sm text-text-secondary leading-relaxed">
                                    {pillar.description}
                                </p>
                            </div>
                        </div>
                    </FadeInStaggerChild>
                ))}
            </FadeInStagger>

            {/* Spreader GIF - terraforming in action */}
            <FadeIn delay={0.3}>
                <div className="rounded-xl overflow-hidden border border-border bg-surface">
                    <VideoClip
                        src="/media/shot_spreader.mp4"
                        alt="Terraforming spreaders reshaping the Martian landscape"
                        className="w-full h-auto block"
                    />
                    <div className="px-5 py-3 border-t border-border">
                        <p className="text-sm font-semibold text-text">{t.pillars.videoCaption}</p>
                        <p className="text-xs text-text-secondary mt-0.5">
                            {t.pillars.videoDescription}
                        </p>
                    </div>
                </div>
            </FadeIn>
        </SectionWrapper>
    );
}
